// Lost Reasons (Kaybedilme Nedenleri) — yönetim sayfası + rapor.
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Button, Modal, FormField, ListPageHeader, EmptyState } from '../components/ui';
import { toast } from '../components/Toast';
import { fmtAmount } from '../utils/numberFormat';

export default function LostReasonsPage() {
  const [list, setList] = useState([]);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ label: '', description: '', sort_order: 100 });

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [lR, rR] = await Promise.all([
        api.get('/crm/lost-reasons'),
        api.get('/crm/lost-reasons-report'),
      ]);
      if (lR.data.success) setList(lR.data.data || []);
      if (rR.data.success) setReport(rR.data.data || []);
    } catch (e) { toast.error('Yüklenemedi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openNew = () => {
    setEditing(null);
    setForm({ label: '', description: '', sort_order: (list.length + 1) * 10 });
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ label: r.label, description: r.description || '', sort_order: r.sort_order });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.label.trim()) { toast.error('Etiket zorunlu'); return; }
    try {
      if (editing) await api.put('/crm/lost-reasons/' + editing.id, form);
      else await api.post('/crm/lost-reasons', form);
      toast.success(editing ? 'Güncellendi' : 'Eklendi');
      setShowForm(false);
      fetchAll();
    } catch (e) { toast.error('Hata'); }
  };

  const remove = async (r) => {
    if (!window.confirm(`"${r.label}" silinsin mi?`)) return;
    try {
      await api.delete('/crm/lost-reasons/' + r.id);
      toast.success('Silindi');
      fetchAll();
    } catch (e) { toast.error('Hata'); }
  };

  // Rapor toplamları
  const totalLostCount = report.reduce((a, b) => a + (b.count || 0), 0);
  const totalLostValue = report.reduce((a, b) => a + parseFloat(b.total_lost_value || 0), 0);

  return (
    <div className="stocks-page">
      <ListPageHeader
        title="Kaybedilme Nedenleri"
        unit="neden"
        count={list.length}
        onRefresh={fetchAll}
        refreshing={loading}
        actions={<Button size="sm" onClick={openNew}>+ Yeni Neden</Button>}
      />

      <div style={{ padding: 16 }}>
        {/* Rapor özeti */}
        {report.length > 0 && (totalLostCount > 0 || totalLostValue > 0) && (
          <div style={S.reportBox}>
            <div style={S.reportTitle}>📊 Kayıp Analizi (toplam {totalLostCount} lead, {fmtAmount(totalLostValue)} ₺ kayıp)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {report.filter(r => r.count > 0).sort((a, b) => b.count - a.count).map(r => {
                const pct = totalLostCount > 0 ? (r.count / totalLostCount) * 100 : 0;
                return (
                  <div key={r.id}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 'var(--text-sm)', marginBottom: 2 }}>
                      <strong>{r.label}</strong>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {r.count} ({pct.toFixed(0)}%)
                        {r.total_lost_value > 0 && <span style={{ color: 'var(--danger)', marginLeft: 8 }}>−{fmtAmount(r.total_lost_value)} ₺</span>}
                      </span>
                    </div>
                    <div style={{ height: 8, background: 'var(--bg-tertiary)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: pct + '%', background: 'var(--danger)', opacity: 0.6, transition: 'width 0.3s' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Liste */}
        {!loading && list.length === 0 ? (
          <EmptyState icon="❌" title="Kaybedilme nedeni tanımlanmamış" description='"+ Yeni Neden" ile başlayın. Yaygın örnekler: Fiyat, Rakip seçildi, İhtiyaç değişti, Ulaşılamadı.' />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
            {list.map(r => (
              <div key={r.id} style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                  <strong style={{ color: 'var(--text-primary)', fontSize: 'var(--text-md)' }}>{r.label}</strong>
                  <span style={S.sortBadge}>#{r.sort_order}</span>
                </div>
                {r.description && (
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 10, lineHeight: 1.4 }}>
                    {r.description}
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                  <Button size="xs" variant="ghost" onClick={() => openEdit(r)}>✎ Düzenle</Button>
                  <Button size="xs" variant="ghost" onClick={() => remove(r)}>🗑 Sil</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <Modal title={editing ? 'Nedeni Düzenle' : 'Yeni Kaybedilme Nedeni'} size="md" onClose={() => setShowForm(false)}
          footer={
            <div className="ui-modal-footer-right" style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" onClick={() => setShowForm(false)}>İptal</Button>
              <Button onClick={save}>💾 Kaydet</Button>
            </div>
          }>
          <div className="form-grid">
            <FormField label="Etiket" required>
              <input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="örn. Fiyat çok yüksek" />
            </FormField>
            <FormField label="Sıra No">
              <input type="number" value={form.sort_order} onChange={e => setForm({ ...form, sort_order: e.target.value })} />
            </FormField>
            <FormField label="Açıklama" fullWidth>
              <textarea rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Bu neden ne zaman seçilmeli? (opsiyonel açıklama)" />
            </FormField>
          </div>
        </Modal>
      )}
    </div>
  );
}

const S = {
  reportBox: {
    padding: 14,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    marginBottom: 14,
    boxShadow: 'var(--shadow-xs)',
  },
  reportTitle: {
    fontSize: 'var(--text-sm)',
    fontWeight: 700,
    color: 'var(--text-primary)',
    marginBottom: 10,
  },
  card: {
    padding: 12,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-secondary)',
    borderRadius: 'var(--r-md)',
    boxShadow: 'var(--shadow-xs)',
  },
  sortBadge: {
    fontSize: '0.65rem',
    fontWeight: 700,
    color: 'var(--text-tertiary)',
    background: 'var(--bg-tertiary)',
    padding: '2px 6px',
    borderRadius: 'var(--r-sm)',
    fontFamily: 'JetBrains Mono, monospace',
  },
};
